/**
 * Pass-one client onboarding.
 *
 * This first pass does one job: get a real order source connected, check the
 * first import, and open the revenue / Gross LTV dashboard. Product costs,
 * overhead, cash inputs, and team verification remain available in their
 * existing routes as a separate second pass.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConnectModal from "../components/ConnectModal.js";
import { useApiClient } from "../lib/api-context.js";
import { useIntegrationCatalog, type CatalogProvider } from "../lib/integration-catalog.js";
import { isOrderSourceProvider } from "../lib/onboarding-connection-groups.js";
import { selectSafeImportRetryTargets } from "../lib/onboarding-import-retry.js";
import { ONBOARDING_PREVIEW_PROVIDERS } from "../lib/onboarding-preview-catalog.js";
import { relockFinancialQueries } from "../lib/query-invalidation.js";
import {
  hasActiveSampleDataJob,
  shouldAutoLoadTestSampleData,
} from "../lib/onboarding-sample-data.js";
import {
  getAllowedOnboardingStep,
  keepFarthestOnboardingStep,
  ONBOARDING_STEP_COUNT,
  singleOrderSourceSelection,
} from "../lib/onboarding-step-access.js";
import { useToast } from "../lib/toast.js";
import { useViewportWidth } from "../lib/use-viewport.js";

interface MissingPlatformRequest {
  platformName: string;
  note: string;
  dataType: "sales_orders";
  preference: "continue";
  requestedAt: string;
}

interface Journey {
  currentStep: number;
  selectedPlatforms: string[];
  guidedStartedAt: string | null;
  missingPlatformRequest: MissingPlatformRequest | null;
}

interface JourneyResponse {
  journey: Journey;
}

interface GuidedAccount {
  name?: string | null;
  timeZone?: string | null;
  baseCurrency?: string | null;
}

interface ImportHealth {
  status: "pending" | "ready" | "error";
  lastSyncedAt: string | null;
  errorMessage: string | null;
  skuCount: number;
  sources: Array<{ platform: string; lastSuccessfulSyncAt: string | null; error: string | null }>;
  unresolvedFailures: number;
  duplicateCount: number;
  duplicateCandidates: number;
  activeJobs: Array<{
    id: string;
    jobType: string;
    status: string;
    progressCurrent: number | null;
    progressTotal: number | null;
  }>;
}

/** Gross results open as soon as real orders land; cleanup checks continue in the background. */
export function canOpenGrossDashboard(status: ImportHealth["status"] | undefined): boolean {
  return status === "ready";
}

export function salesSourceSummary(platforms: string[]): string {
  if (platforms.length === 0) return "Waiting for the first proven sales source";
  const labels = platforms.map(prettyPlatform);
  const sourceList = labels.length === 1
    ? labels[0]
    : labels.length === 2
      ? `${labels[0]} and ${labels[1]}`
      : `${labels.slice(0, -1).join(", ")}, and ${labels.at(-1)}`;
  return `Sales data found from ${sourceList}`;
}

const STEPS = [
  { n: 1, label: "Welcome" },
  { n: 2, label: "Choose sales source" },
  { n: 3, label: "Connect" },
  { n: 4, label: "Check import" },
] as const;

const DEFAULT_JOURNEY: Journey = {
  currentStep: 1,
  selectedPlatforms: [],
  guidedStartedAt: null,
  missingPlatformRequest: null,
};

const BROWSER_TIME_ZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
})();

const TIME_ZONES = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return [BROWSER_TIME_ZONE];
  }
})();

const COMMON_CURRENCIES = ["USD", "CAD", "GBP", "EUR", "AUD", "NZD"];
const CURRENCIES = (() => {
  try {
    return Intl.supportedValuesOf("currency");
  } catch {
    return COMMON_CURRENCIES;
  }
})();

const PREVIEW_HEALTH_READY: ImportHealth = {
  status: "ready",
  lastSyncedAt: new Date().toISOString(),
  errorMessage: null,
  skuCount: 15,
  sources: [{ platform: "shopify", lastSuccessfulSyncAt: new Date().toISOString(), error: null }],
  unresolvedFailures: 0,
  duplicateCount: 37,
  duplicateCandidates: 0,
  activeJobs: [],
};

const PREVIEW_HEALTH_PENDING: ImportHealth = {
  ...PREVIEW_HEALTH_READY,
  status: "pending",
  lastSyncedAt: null,
  skuCount: 0,
  sources: [{ platform: "shopify", lastSuccessfulSyncAt: null, error: null }],
  duplicateCount: 0,
  activeJobs: [
    {
      id: "preview-import",
      jobType: "orders",
      status: "running",
      progressCurrent: 3_240,
      progressTotal: 8_900,
    },
  ],
};

function mergeJourney(current: Journey, patch: Partial<Journey>): Journey {
  return { ...current, ...patch };
}

export default function GuidedOnboardingPage({
  forcePreview = false,
}: {
  forcePreview?: boolean;
}): React.ReactElement {
  const api = useApiClient();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const compact = useViewportWidth() < 900;
  const [params, setParams] = useSearchParams();
  const previewMode = forcePreview;
  const requestedStepNumber = Number(params.get("step"));
  const requestedStep =
    Number.isInteger(requestedStepNumber) &&
    requestedStepNumber >= 1 &&
    requestedStepNumber <= ONBOARDING_STEP_COUNT
      ? requestedStepNumber
      : null;

  const [previewStep, setPreviewStep] = useState(requestedStep ?? 1);
  const [previewConnected, setPreviewConnected] = useState<Set<string>>(new Set());
  const [previewImportReady, setPreviewImportReady] = useState(false);
  const [draftJourney, setDraftJourney] = useState<Journey>(DEFAULT_JOURNEY);
  const draftJourneyRef = useRef(DEFAULT_JOURNEY);
  const journeyHydrated = useRef(false);
  const pendingSaves = useRef<globalThis.Promise<unknown>>(globalThis.Promise.resolve());
  const latestSave = useRef(0);
  const pendingSaveCount = useRef(0);
  const [journeySaving, setJourneySaving] = useState(false);
  const [navigationBusy, setNavigationBusy] = useState(false);
  const navigationBusyRef = useRef(false);
  const lastNavigationAction = useRef<(() => Promise<void>) | null>(null);
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const [modalPlatform, setModalPlatform] = useState<string | null>(null);
  const [sampleDataQueued, setSampleDataQueued] = useState(false);
  const [sampleDataStartedAt, setSampleDataStartedAt] = useState<number | null>(null);

  const [accountHydrated, setAccountHydrated] = useState(previewMode);
  const [businessName, setBusinessName] = useState(previewMode ? "Vital Roots" : "");
  const [timeZone, setTimeZone] = useState(BROWSER_TIME_ZONE);
  const [baseCurrency, setBaseCurrency] = useState("USD");

  const journeyQ = useQuery({
    queryKey: ["onboarding", "journey"],
    enabled: !previewMode,
    queryFn: () => api.get<JourneyResponse>("/onboarding/journey"),
  });
  const accountQ = useQuery({
    queryKey: ["account"],
    enabled: !previewMode,
    queryFn: () => api.get<{ account: GuidedAccount }>("/account"),
  });
  const integrationsQ = useQuery({
    queryKey: ["integrations"],
    enabled: !previewMode,
    queryFn: () =>
      api.get<{ integrations: Array<{ platform: string; status: string }> }>("/integrations"),
    refetchInterval: sampleDataQueued ? 3_000 : false,
  });
  const agencyStatusQ = useQuery({
    queryKey: ["account", "agency-status"],
    enabled: !previewMode,
    queryFn: () => api.get<{ resettable: boolean }>("/account/agency-status"),
  });
  const catalogQ = useIntegrationCatalog(!previewMode);

  useEffect(() => {
    if (previewMode || !journeyQ.data?.journey) return;
    if (!journeyHydrated.current || pendingSaveCount.current === 0) {
      journeyHydrated.current = true;
      draftJourneyRef.current = journeyQ.data.journey;
      setDraftJourney(journeyQ.data.journey);
    }
  }, [journeyQ.data?.journey, previewMode]);

  useEffect(() => {
    if (previewMode || accountHydrated || !accountQ.data?.account) return;
    const account = accountQ.data.account;
    setBusinessName(account.name ?? "");
    setTimeZone(account.timeZone ?? BROWSER_TIME_ZONE);
    setBaseCurrency(account.baseCurrency ?? "USD");
    setAccountHydrated(true);
  }, [accountHydrated, accountQ.data?.account, previewMode]);

  const savedStep = previewMode
    ? previewStep
    : getAllowedOnboardingStep(null, draftJourney.currentStep);
  const currentStep = previewMode
    ? previewStep
    : getAllowedOnboardingStep(requestedStep, savedStep);

  useEffect(() => {
    if (!previewMode || currentStep !== 4 || previewImportReady) return;
    const timer = window.setTimeout(() => setPreviewImportReady(true), 4_000);
    return () => window.clearTimeout(timer);
  }, [currentStep, previewImportReady, previewMode]);

  const healthQ = useQuery({
    queryKey: ["onboarding", "import-health"],
    enabled:
      !previewMode &&
      (currentStep >= 4 || sampleDataQueued || agencyStatusQ.data?.resettable === true),
    queryFn: () => api.get<ImportHealth>("/onboarding/import-health"),
    refetchInterval: (query) =>
      query.state.data?.status === "pending" || hasActiveSampleDataJob(query.state.data?.activeJobs)
        ? 3_000
        : false,
  });

  const saveJourney = async (patch: Partial<Journey>): Promise<Journey> => {
    const optimistic = mergeJourney(draftJourneyRef.current, patch);
    draftJourneyRef.current = optimistic;
    setDraftJourney(optimistic);
    if (previewMode) return optimistic;

    pendingSaveCount.current += 1;
    setJourneySaving(true);
    const saveNumber = latestSave.current + 1;
    latestSave.current = saveNumber;
    const request = pendingSaves.current
      .catch(() => undefined)
      .then(() => api.patch<JourneyResponse>("/onboarding/journey", patch));
    pendingSaves.current = request.then(
      () => undefined,
      () => undefined,
    );

    try {
      const response = await request;
      if (saveNumber === latestSave.current) {
        draftJourneyRef.current = response.journey;
        setDraftJourney(response.journey);
        void queryClient.invalidateQueries({ queryKey: ["onboarding", "journey"] });
      }
      return response.journey;
    } catch (error) {
      if (saveNumber === latestSave.current) {
        const refreshed = await journeyQ.refetch();
        if (refreshed.data?.journey) {
          draftJourneyRef.current = refreshed.data.journey;
          setDraftJourney(refreshed.data.journey);
        }
      }
      throw error;
    } finally {
      pendingSaveCount.current -= 1;
      setJourneySaving(pendingSaveCount.current > 0);
    }
  };

  const goTo = async (nextStep: number, patch: Partial<Journey> = {}): Promise<void> => {
    if (previewMode) {
      setPreviewStep(nextStep);
    } else {
      await saveJourney({
        ...patch,
        currentStep: keepFarthestOnboardingStep(draftJourneyRef.current.currentStep, nextStep),
      });
    }
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("step", String(nextStep));
        return next;
      },
      { replace: true },
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const runNavigation = async (action: () => Promise<void>): Promise<void> => {
    if (navigationBusyRef.current) return;
    navigationBusyRef.current = true;
    lastNavigationAction.current = action;
    setNavigationBusy(true);
    setNavigationError(null);
    try {
      await action();
    } catch {
      setNavigationError("We couldn't save your progress. Nothing was lost. Try again.");
    } finally {
      navigationBusyRef.current = false;
      setNavigationBusy(false);
    }
  };

  const allProviders = previewMode
    ? ONBOARDING_PREVIEW_PROVIDERS
    : (catalogQ.data?.providers ?? []);
  const orderProviders = useMemo(
    () =>
      allProviders
        .filter((provider) => provider.showInOnboarding && isOrderSourceProvider(provider))
        .filter((provider) => !provider.comingSoon || provider.reviewWithUs)
        .sort((left, right) => left.label.localeCompare(right.label)),
    [allProviders],
  );
  const activePlatforms = useMemo(
    () =>
      new Set(
        previewMode
          ? previewConnected
          : (integrationsQ.data?.integrations ?? [])
              .filter((integration) => integration.status === "active")
              .map((integration) => integration.platform),
      ),
    [integrationsQ.data?.integrations, previewConnected, previewMode],
  );
  const selectedPlatform =
    draftJourney.selectedPlatforms.find((platform) =>
      orderProviders.some((provider) => provider.platform === platform),
    ) ??
    orderProviders.find((provider) => activePlatforms.has(provider.platform))?.platform ??
    null;
  const selectedProvider =
    orderProviders.find((provider) => provider.platform === selectedPlatform) ?? null;

  const selectOrderSource = (platform: string): void => {
    void saveJourney({
      selectedPlatforms: singleOrderSourceSelection(platform),
      missingPlatformRequest: null,
    }).catch(() => toast.error("We couldn't save that choice. Try it once more."));
  };

  const requestMissingOrderSource = async (platformName: string): Promise<void> => {
    const trimmed = platformName.trim();
    if (!trimmed) throw new Error("MISSING_PLATFORM_NAME");
    const request: MissingPlatformRequest = {
      platformName: trimmed,
      note: "This platform holds completed orders and refunds.",
      dataType: "sales_orders",
      preference: "continue",
      requestedAt: new Date().toISOString(),
    };
    if (!previewMode) {
      await api.post("/integrations/connection-request", {
        platformName: trimmed,
        note: "This platform holds completed orders and refunds.",
      });
    }
    await saveJourney({ selectedPlatforms: [], missingPlatformRequest: request });
  };

  const importRetryTargets = useMemo(
    () => selectSafeImportRetryTargets(orderProviders, activePlatforms),
    [activePlatforms, orderProviders],
  );
  const retryImport = useMutation({
    mutationFn: async () => {
      if (importRetryTargets.length === 0) throw new Error("NO_SAFE_IMPORT_ACTION");
      for (const target of importRetryTargets) {
        await api.post(`/sync-jobs/${target.platform}/${target.actionId}`);
      }
    },
    onSuccess: () => toast.success("Import restarted. This page will update as data arrives."),
    onError: () =>
      toast.error(
        importRetryTargets.length === 0
          ? "Reopen your sales connection so we can restart the import."
          : "We couldn't restart the import. Check the connection and try again.",
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "import-health"] });
      void healthQ.refetch();
    },
  });

  const sampleDataJobActive = hasActiveSampleDataJob(healthQ.data?.activeJobs);
  const sampleDataBusy = sampleDataQueued || sampleDataJobActive;
  const loadSampleData = useMutation({
    mutationFn: (platform: string) =>
      api.post("/account/seed-sample-data", {
        guidedOnboarding: true,
        samplePlatforms: singleOrderSourceSelection(platform),
      }),
    onSuccess: () => {
      relockFinancialQueries(queryClient);
      setSampleDataQueued(true);
      setSampleDataStartedAt(Date.now());
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "import-health"] });
    },
  });

  useEffect(() => {
    if (!sampleDataQueued || healthQ.isLoading || sampleDataJobActive || !healthQ.data) return;
    const lastSyncAt = healthQ.data.lastSyncedAt
      ? new Date(healthQ.data.lastSyncedAt).getTime()
      : Number.NaN;
    const finishedAfterStart =
      sampleDataStartedAt !== null &&
      Number.isFinite(lastSyncAt) &&
      lastSyncAt >= sampleDataStartedAt;
    if (healthQ.data.status !== "error" && !finishedAfterStart) return;
    setSampleDataQueued(false);
    setSampleDataStartedAt(null);
  }, [healthQ.data, healthQ.isLoading, sampleDataJobActive, sampleDataQueued, sampleDataStartedAt]);

  const saveAccountAndContinue = async (): Promise<void> => {
    if (!businessName.trim() || !timeZone || !baseCurrency) {
      throw new Error("MISSING_ACCOUNT_BASICS");
    }
    if (!previewMode) {
      await api.patch("/account", {
        name: businessName.trim(),
        timeZone,
        baseCurrency,
      });
      relockFinancialQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["account"] });
      void queryClient.invalidateQueries({ queryKey: ["onboarding", "state"] });
    }
    await goTo(2, {
      guidedStartedAt: draftJourneyRef.current.guidedStartedAt ?? new Date().toISOString(),
    });
  };

  const continueFromSourceChoice = async (): Promise<void> => {
    if (!selectedPlatform && !draftJourneyRef.current.missingPlatformRequest) {
      throw new Error("MISSING_ORDER_SOURCE");
    }
    if (selectedPlatform) {
      await saveJourney({ selectedPlatforms: singleOrderSourceSelection(selectedPlatform) });
      if (
        shouldAutoLoadTestSampleData({
          isTestAccount: agencyStatusQ.data?.resettable === true,
          previewMode,
          sampleDataBusy: sampleDataBusy || loadSampleData.isPending,
          hasSampleStoreConnection: activePlatforms.has("ultracart"),
        })
      ) {
        await loadSampleData.mutateAsync(selectedPlatform);
      }
    }
    await goTo(3);
  };

  const continueFromConnection = async (): Promise<void> => {
    await goTo(4);
  };

  const previewHealthBase = previewImportReady ? PREVIEW_HEALTH_READY : PREVIEW_HEALTH_PENDING;
  const previewHealth: ImportHealth = {
    ...previewHealthBase,
    sources: [{
      platform: selectedPlatform ?? "shopify",
      lastSuccessfulSyncAt: previewImportReady ? new Date().toISOString() : null,
      error: null,
    }],
  };

  if (
    !previewMode &&
    (journeyQ.isLoading || accountQ.isLoading || catalogQ.isLoading || !accountHydrated)
  ) {
    return <LoadingBlock label="Loading your setup…" />;
  }
  if (
    !previewMode &&
    (journeyQ.isError ||
      accountQ.isError ||
      catalogQ.isError ||
      (currentStep === 4 && healthQ.isError))
  ) {
    return (
      <ErrorBlock
        onRetry={() => {
          void journeyQ.refetch();
          void accountQ.refetch();
          void catalogQ.refetch();
          if (currentStep === 4) void healthQ.refetch();
        }}
      />
    );
  }

  let panel: React.ReactElement;
  switch (currentStep) {
    case 1:
      panel = (
        <WelcomeStep
          businessName={businessName}
          timeZone={timeZone}
          baseCurrency={baseCurrency}
          onBusinessNameChange={setBusinessName}
          onTimeZoneChange={setTimeZone}
          onBaseCurrencyChange={setBaseCurrency}
          onContinue={() => void runNavigation(saveAccountAndContinue)}
        />
      );
      break;
    case 2:
      panel = (
        <ChooseOrderSourceStep
          providers={orderProviders}
          selectedPlatform={selectedPlatform}
          activePlatforms={activePlatforms}
          missingRequest={draftJourney.missingPlatformRequest}
          onSelect={selectOrderSource}
          onRequest={requestMissingOrderSource}
          onBack={() => void runNavigation(() => goTo(1))}
          onContinue={() => void runNavigation(continueFromSourceChoice)}
        />
      );
      break;
    case 3:
      panel = (
        <ConnectOrderSourceStep
          provider={selectedProvider}
          connected={Boolean(selectedProvider && activePlatforms.has(selectedProvider.platform))}
          missingRequest={draftJourney.missingPlatformRequest}
          preparingTestData={sampleDataBusy || loadSampleData.isPending}
          previewMode={previewMode}
          onConnect={(platform) => {
            if (previewMode) {
              setPreviewConnected(new Set([platform]));
              toast.success("This source looks connected in the preview.");
            } else {
              setModalPlatform(platform);
            }
          }}
          onBack={() => void runNavigation(() => goTo(2))}
          onContinue={() => void runNavigation(continueFromConnection)}
        />
      );
      break;
    case 4:
    default:
      panel = (
        <ImportCheckStep
          health={previewMode ? previewHealth : healthQ.data}
          loading={!previewMode && healthQ.isLoading}
          previewMode={previewMode}
          retrying={retryImport.isPending}
          canRestart={importRetryTargets.length > 0}
          onBack={() => void runNavigation(() => goTo(3))}
          onRetry={() => retryImport.mutate()}
          onRefresh={() => void healthQ.refetch()}
          onReviewConnection={() => void runNavigation(() => goTo(3))}
          onOpenDashboard={() => {
            if (previewMode) {
              navigate("/demo/cohorts");
              return;
            }
            void queryClient.invalidateQueries({ queryKey: ["onboarding", "state"] });
            void queryClient.invalidateQueries({ queryKey: ["dashboard", "status"] });
            navigate("/cohorts");
          }}
        />
      );
      break;
  }

  return (
    <div style={{ maxWidth: 1120, margin: "0 auto" }}>
      {previewMode && (
        <div style={previewBanner}>
          <b>Clickable preview.</b> This uses example data. Nothing here connects or changes a real
          account.
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={eyebrow}>QUICK START</div>
          <h1 style={{ margin: "4px 0 6px", fontSize: 28 }}>Get your gross dashboard live</h1>
          <p style={{ margin: 0, color: "var(--body)", fontSize: 14 }}>
            Connect your order source first. We&apos;ll set up expenses separately after your
            revenue and Gross LTV are visible.
          </p>
        </div>
        <span style={{ fontSize: 13, color: "var(--body)", fontWeight: 650 }}>
          Step {currentStep} of {ONBOARDING_STEP_COUNT}
        </span>
      </div>

      <div style={{ ...progressTrack, marginTop: 18 }}>
        <div
          style={{
            ...progressFill,
            width: `${((currentStep - 1) / (ONBOARDING_STEP_COUNT - 1)) * 100}%`,
          }}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: compact ? "1fr" : "210px minmax(0, 1fr)",
          gap: compact ? 16 : 28,
          marginTop: 24,
          alignItems: "start",
        }}
      >
        <nav
          aria-label="Setup steps"
          style={{
            ...stepNav,
            position: compact ? "static" : "sticky",
            top: 88,
            ...(compact ? { flexDirection: "row", overflowX: "auto", padding: 6 } : {}),
          }}
        >
          {STEPS.map((item) => {
            const done = item.n < savedStep;
            const active = item.n === currentStep;
            const unlocked = previewMode || item.n <= savedStep;
            return (
              <button
                key={item.n}
                type="button"
                disabled={!unlocked}
                onClick={() => {
                  if (unlocked) void runNavigation(() => goTo(item.n));
                }}
                style={{
                  ...stepButton,
                  ...(compact
                    ? { width: "auto", minWidth: "max-content", whiteSpace: "nowrap" }
                    : {}),
                  ...(active ? stepButtonActive : {}),
                  opacity: unlocked ? 1 : 0.45,
                }}
              >
                <span style={{ ...stepDot, ...(done ? stepDotDone : active ? stepDotActive : {}) }}>
                  {done ? "✓" : item.n}
                </span>
                <span style={{ textAlign: "left" }}>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <main>
          {(navigationBusy || journeySaving) && (
            <div style={{ ...infoBox, marginBottom: 12 }}>
              <Spinner /> <b>Saving your progress…</b>
            </div>
          )}
          {navigationError && (
            <div role="alert" style={{ ...warningBox, marginTop: 0, marginBottom: 12 }}>
              <b>{navigationError}</b>
              <button
                type="button"
                style={{ ...secondaryButton, marginLeft: 10 }}
                onClick={() => {
                  const retry = lastNavigationAction.current;
                  if (retry) void runNavigation(retry);
                }}
              >
                Try again
              </button>
            </div>
          )}
          {panel}
        </main>
      </div>

      {modalPlatform && (
        <ConnectModal
          platform={modalPlatform}
          provider={orderProviders.find((provider) => provider.platform === modalPlatform)}
          onClose={() => setModalPlatform(null)}
          onSuccess={() => {
            setModalPlatform(null);
            void queryClient.invalidateQueries({ queryKey: ["integrations"] });
            void queryClient.invalidateQueries({ queryKey: ["onboarding", "state"] });
            void queryClient.invalidateQueries({ queryKey: ["onboarding", "import-health"] });
          }}
        />
      )}
    </div>
  );
}

function WelcomeStep({
  businessName,
  timeZone,
  baseCurrency,
  onBusinessNameChange,
  onTimeZoneChange,
  onBaseCurrencyChange,
  onContinue,
}: {
  businessName: string;
  timeZone: string;
  baseCurrency: string;
  onBusinessNameChange: (value: string) => void;
  onTimeZoneChange: (value: string) => void;
  onBaseCurrencyChange: (value: string) => void;
  onContinue: () => void;
}): React.ReactElement {
  const ready = Boolean(businessName.trim() && timeZone && baseCurrency);
  return (
    <StepFrame
      title="First, confirm your business"
      intro="We use these details to place sales in the right day and show money in the right currency. Everything already saved is filled in for you."
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
          gap: 12,
        }}
      >
        <Field label="Business name">
          <input
            style={inputStyle}
            value={businessName}
            onChange={(event) => onBusinessNameChange(event.target.value)}
          />
        </Field>
        <Field label="Time zone">
          <select
            style={inputStyle}
            value={timeZone}
            onChange={(event) => onTimeZoneChange(event.target.value)}
          >
            {!TIME_ZONES.includes(timeZone) && <option value={timeZone}>{timeZone}</option>}
            {TIME_ZONES.map((zone) => (
              <option key={zone} value={zone}>
                {zone.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Business currency">
          <select
            style={inputStyle}
            value={baseCurrency}
            onChange={(event) => onBaseCurrencyChange(event.target.value)}
          >
            {COMMON_CURRENCIES.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
            <option disabled>──────────</option>
            {CURRENCIES.filter((currency) => !COMMON_CURRENCIES.includes(currency)).map(
              (currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ),
            )}
          </select>
        </Field>
      </div>
      <div style={{ ...infoBox, marginTop: 16 }}>
        <b>This first pass is only for gross numbers.</b> Product costs, overhead, and cash timing
        come later and will not affect what you see yet.
      </div>
      <StepActions
        onContinue={onContinue}
        continueLabel="Choose my sales source"
        disabled={!ready}
      />
    </StepFrame>
  );
}

function ChooseOrderSourceStep({
  providers,
  selectedPlatform,
  activePlatforms,
  missingRequest,
  onSelect,
  onRequest,
  onBack,
  onContinue,
}: {
  providers: CatalogProvider[];
  selectedPlatform: string | null;
  activePlatforms: ReadonlySet<string>;
  missingRequest: MissingPlatformRequest | null;
  onSelect: (platform: string) => void;
  onRequest: (platformName: string) => Promise<void>;
  onBack: () => void;
  onContinue: () => void;
}): React.ReactElement {
  const [search, setSearch] = useState("");
  const [missingName, setMissingName] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const normalizedSearch = search.trim().toLowerCase();
  const visible = normalizedSearch
    ? providers.filter((provider) =>
        `${provider.label} ${provider.description}`.toLowerCase().includes(normalizedSearch),
      )
    : providers;
  const covered = Boolean(selectedPlatform || missingRequest);

  const submitRequest = async (): Promise<void> => {
    if (!missingName.trim()) {
      setRequestError("Enter the name of the platform that holds your orders.");
      return;
    }
    setRequesting(true);
    setRequestError(null);
    try {
      await onRequest(missingName);
    } catch {
      setRequestError("We couldn't send that request. Nothing changed. Try again.");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <StepFrame
      title="Where do your completed orders live?"
      intro="Choose one source—the main system that holds completed orders and refunds. Other connections can wait until later."
    >
      <input
        type="search"
        aria-label="Search sales sources"
        placeholder="Search sales sources"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        style={{ ...inputStyle, width: "100%", marginBottom: 12 }}
      />
      <div
        role="radiogroup"
        aria-label="Sales source"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
          gap: 9,
        }}
      >
        {visible.map((provider) => {
          const selected = selectedPlatform === provider.platform;
          const connected = activePlatforms.has(provider.platform);
          return (
            <label
              key={provider.platform}
              style={{ ...choiceRow, ...(selected ? choiceRowSelected : {}) }}
            >
              <input
                type="radio"
                name="order-source"
                value={provider.platform}
                checked={selected}
                onChange={() => onSelect(provider.platform)}
              />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                  <b>{provider.label}</b>
                  {connected && <span style={goodPill}>Connected</span>}
                </span>
                <span style={{ ...muted, display: "block", marginTop: 3 }}>
                  {provider.description}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      {visible.length === 0 && <div style={emptyBox}>No matching sales source found.</div>}

      <div style={{ ...targetBox, marginTop: 16 }}>
        <div style={{ fontWeight: 800 }}>Don&apos;t see your sales source?</div>
        {missingRequest ? (
          <div style={infoBox}>
            <b>Request sent for {missingRequest.platformName}.</b>
            <div style={{ ...muted, marginTop: 4 }}>
              We&apos;ll help connect it before your gross dashboard can open.
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              style={{ ...inputStyle, flex: "1 1 250px" }}
              value={missingName}
              onChange={(event) => setMissingName(event.target.value)}
              placeholder="Platform name"
            />
            <button
              type="button"
              style={secondaryButton}
              disabled={requesting}
              onClick={() => void submitRequest()}
            >
              {requesting ? "Sending…" : "Request this connection"}
            </button>
          </div>
        )}
        {requestError && (
          <div role="alert" style={{ color: "var(--bad)", fontSize: 12.5 }}>
            {requestError}
          </div>
        )}
      </div>
      <StepActions
        onBack={onBack}
        onContinue={onContinue}
        continueLabel="Continue to connection"
        disabled={!covered}
      />
    </StepFrame>
  );
}

function ConnectOrderSourceStep({
  provider,
  connected,
  missingRequest,
  preparingTestData,
  previewMode,
  onConnect,
  onBack,
  onContinue,
}: {
  provider: CatalogProvider | null;
  connected: boolean;
  missingRequest: MissingPlatformRequest | null;
  preparingTestData: boolean;
  previewMode: boolean;
  onConnect: (platform: string) => void;
  onBack: () => void;
  onContinue: () => void;
}): React.ReactElement {
  return (
    <StepFrame
      title={provider ? `Connect ${provider.label}` : "Your sales connection needs help"}
      intro="This is the only connection needed to start importing revenue, orders, refunds, and customers."
    >
      {provider ? (
        <div style={plainRow}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 750, fontSize: 15 }}>{provider.label}</div>
            <div style={{ ...muted, marginTop: 3 }}>{provider.description}</div>
          </div>
          {connected ? (
            <span style={goodPill}>Connected ✓</span>
          ) : preparingTestData ? (
            <span style={workingPill}>
              <Spinner /> Preparing test data
            </span>
          ) : provider.reviewWithUs ? (
            <span style={workingPill}>We&apos;ll help connect it</span>
          ) : (
            <button
              type="button"
              style={primaryButton}
              onClick={() => onConnect(provider.platform)}
            >
              Connect {provider.label}
            </button>
          )}
        </div>
      ) : missingRequest ? (
        <div style={warningBox}>
          <b>{missingRequest.platformName} is not available as a self-serve connection yet.</b>
          <div style={{ ...muted, marginTop: 4 }}>
            Your request is saved. We&apos;ll help connect it before the import can start.
          </div>
        </div>
      ) : (
        <div style={emptyBox}>Go back and choose the source that holds your orders.</div>
      )}
      {previewMode && (
        <div style={{ ...muted, marginTop: 12 }}>
          The preview marks this connected without asking for a real login.
        </div>
      )}
      <StepActions
        onBack={onBack}
        onContinue={onContinue}
        continueLabel="Check my import"
        disabled={!provider || !connected}
      />
    </StepFrame>
  );
}

function ImportCheckStep({
  health,
  loading,
  previewMode,
  retrying,
  canRestart,
  onBack,
  onRetry,
  onRefresh,
  onReviewConnection,
  onOpenDashboard,
}: {
  health?: ImportHealth;
  loading: boolean;
  previewMode: boolean;
  retrying: boolean;
  canRestart: boolean;
  onBack: () => void;
  onRetry: () => void;
  onRefresh: () => void;
  onReviewConnection: () => void;
  onOpenDashboard: () => void;
}): React.ReactElement {
  const ready = canOpenGrossDashboard(health?.status);
  const hasOpenChecks =
    (health?.unresolvedFailures ?? 0) > 0 ||
    (health?.duplicateCandidates ?? 0) > 0;
  const needsAttention = health?.status === "error" || (health?.unresolvedFailures ?? 0) > 0;
  const activeJob = health?.activeJobs[0];
  const progress =
    activeJob?.progressTotal && activeJob.progressTotal > 0
      ? Math.min(
          100,
          Math.round(((activeJob.progressCurrent ?? 0) / activeJob.progressTotal) * 100),
        )
      : null;

  return (
    <StepFrame
      title={ready ? "Your gross dashboard is ready" : "We're checking your first import"}
      intro="We make sure sales arrived, failed records are visible, and the same sale is not counted twice. This page updates while the import runs."
    >
      {loading && <LoadingBlock label="Checking your connection and first import…" />}
      {health && (
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          <CheckRow
            label="Sales data"
            detail={salesSourceSummary(health.sources.map((source) => source.platform))}
            state={health.sources.length > 0 ? "done" : "working"}
          />
          <CheckRow
            label="First import"
            detail={
              health.status === "ready"
                ? `Last update ${relativeTime(health.lastSyncedAt)}`
                : health.status === "error"
                  ? "The import needs attention"
                  : progress !== null
                    ? `${(activeJob?.progressCurrent ?? 0).toLocaleString()} of ${activeJob?.progressTotal?.toLocaleString()} orders imported (${progress}%)`
                    : "Importing your order history"
            }
            state={
              health.status === "ready"
                ? "done"
                : health.status === "error"
                  ? "attention"
                  : "working"
            }
          />
          <CheckRow
            label="Import failures"
            detail={
              health.unresolvedFailures === 0
                ? "No unresolved failures"
                : `${health.unresolvedFailures} record${health.unresolvedFailures === 1 ? "" : "s"} need another try`
            }
            state={health.unresolvedFailures === 0 ? "done" : "attention"}
          />
          <CheckRow
            label="Duplicate protection"
            detail={
              health.duplicateCandidates > 0
                ? `${health.duplicateCandidates} close match${health.duplicateCandidates === 1 ? "" : "es"} need your decision`
                : health.duplicateCount > 0
                  ? `${health.duplicateCount} duplicate cop${health.duplicateCount === 1 ? "y was" : "ies were"} counted once`
                  : "No duplicate copies found"
            }
            state={health.duplicateCandidates > 0 ? "attention" : "done"}
          />
        </div>
      )}

      {needsAttention && health && (
        <div style={warningBox}>
          <b>{health.errorMessage || "The import needs another try."}</b>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            {canRestart && (
              <button type="button" style={secondaryButton} disabled={retrying} onClick={onRetry}>
                {retrying ? "Restarting…" : "Retry import"}
              </button>
            )}
            <button
              type="button"
              style={secondaryButton}
              disabled={retrying}
              onClick={onReviewConnection}
            >
              Review connection
            </button>
          </div>
        </div>
      )}

      {health && health.duplicateCandidates > 0 && !previewMode && (
        <DuplicateReview onChanged={onRefresh} />
      )}

      {!ready && health?.status === "pending" && (
        <div style={workingBox}>
          <Spinner />
          <div>
            <b>Your import is still running.</b>
            <div style={muted}>You can leave and come back. It keeps working on its own.</div>
          </div>
        </div>
      )}

      {ready && (
        <div style={readyBox}>
          <div>
            <b>Revenue and Gross LTV are ready to view.</b>
            <div style={{ ...muted, marginTop: 4 }}>
              {hasOpenChecks
                ? "The checks above will keep running, so these early numbers may change. "
                : ""}
              Expenses and profit stay off until the separate expense setup is complete.
            </div>
          </div>
        </div>
      )}

      <StepActions
        onBack={onBack}
        onContinue={onOpenDashboard}
        continueLabel={ready ? "Open my gross dashboard" : "Waiting for the first import"}
        disabled={!ready}
      />
    </StepFrame>
  );
}

interface DedupOverview {
  candidates: Array<{
    aId: string;
    aPlatform: string;
    aExternalId: string;
    aDate: string;
    aAmount: number;
    bId: string;
    bPlatform: string;
    bExternalId: string;
    bDate: string;
    bAmount: number;
    suggestedCanonicalId: string;
  }>;
}

function DuplicateReview({ onChanged }: { onChanged: () => void }): React.ReactElement | null {
  const api = useApiClient();
  const toast = useToast();
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["dedup", "overview"],
    queryFn: () => api.get<DedupOverview>("/dedup/overview"),
  });
  const resolve = useMutation({
    mutationFn: ({
      action,
      candidate,
    }: {
      action: "same" | "different";
      candidate: DedupOverview["candidates"][number];
    }) =>
      action === "same"
        ? api.post("/dedup/link", {
            canonicalOrderId: candidate.suggestedCanonicalId,
            duplicateOrderId:
              candidate.suggestedCanonicalId === candidate.aId ? candidate.bId : candidate.aId,
          })
        : api.post("/dedup/dismiss", { orderAId: candidate.aId, orderBId: candidate.bId }),
    onSuccess: () => {
      relockFinancialQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["dedup", "overview"] });
      onChanged();
    },
    onError: () => toast.error("Couldn't save that decision. Try again."),
  });
  if (!q.data?.candidates.length) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 750 }}>Close matches need your decision</div>
      <div style={{ ...muted, margin: "4px 0 9px" }}>
        “Same sale” counts it once. “Different sales” keeps both.
      </div>
      {q.data.candidates.slice(0, 6).map((candidate) => (
        <div key={`${candidate.aId}:${candidate.bId}`} style={{ ...plainRow, marginTop: 7 }}>
          <div style={{ fontSize: 12.5 }}>
            <div>
              <b>{prettyPlatform(candidate.aPlatform)}</b> #{candidate.aExternalId} · $
              {candidate.aAmount.toFixed(2)}
            </div>
            <div>
              <b>{prettyPlatform(candidate.bPlatform)}</b> #{candidate.bExternalId} · $
              {candidate.bAmount.toFixed(2)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              style={secondaryButton}
              disabled={resolve.isPending}
              onClick={() => resolve.mutate({ action: "different", candidate })}
            >
              Different sales
            </button>
            <button
              type="button"
              style={primaryButtonSmall}
              disabled={resolve.isPending}
              onClick={() => resolve.mutate({ action: "same", candidate })}
            >
              Same sale
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function StepFrame({
  title,
  intro,
  children,
}: {
  title: string;
  intro: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section style={surface}>
      <h2 style={{ fontSize: 25, margin: 0 }}>{title}</h2>
      <p style={{ color: "var(--body)", margin: "8px 0 22px", lineHeight: 1.55, maxWidth: 760 }}>
        {intro}
      </p>
      {children}
    </section>
  );
}

function StepActions({
  onBack,
  onContinue,
  continueLabel,
  disabled = false,
}: {
  onBack?: () => void;
  onContinue: () => void;
  continueLabel: string;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        marginTop: 26,
        paddingTop: 18,
        borderTop: "1px solid var(--border)",
      }}
    >
      {onBack ? (
        <button type="button" style={secondaryButton} onClick={onBack}>
          ← Back
        </button>
      ) : (
        <span />
      )}
      <button
        type="button"
        style={{
          ...primaryButton,
          opacity: disabled ? 0.42 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        disabled={disabled}
        onClick={onContinue}
      >
        {continueLabel} →
      </button>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <label style={fieldStyle}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function CheckRow({
  label,
  detail,
  state,
}: {
  label: string;
  detail: string;
  state: "done" | "working" | "attention";
}): React.ReactElement {
  const icon = state === "done" ? "✓" : state === "attention" ? "!" : <Spinner />;
  const color =
    state === "done" ? "var(--good)" : state === "attention" ? "var(--c2)" : "var(--accent)";
  return (
    <div style={checkRow}>
      <span style={{ ...checkIcon, color, borderColor: color }}>{icon}</span>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13.5 }}>{label}</div>
        <div style={muted}>{detail}</div>
      </div>
    </div>
  );
}

function LoadingBlock({ label }: { label: string }): React.ReactElement {
  return (
    <div style={workingBox}>
      <Spinner />
      <div>
        <b>{label}</b>
        <div style={muted}>This updates as soon as the next part finishes.</div>
      </div>
    </div>
  );
}

function ErrorBlock({ onRetry }: { onRetry: () => void }): React.ReactElement {
  return (
    <div style={{ ...surface, maxWidth: 620 }}>
      <h2 style={{ marginTop: 0 }}>We couldn&apos;t load setup</h2>
      <p style={{ color: "var(--body)" }}>Nothing was lost. Try again in a moment.</p>
      <button type="button" style={primaryButton} onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function Spinner(): React.ReactElement {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 16,
        height: 16,
        borderRadius: "50%",
        border: "2px solid color-mix(in srgb, var(--accent) 22%, transparent)",
        borderTopColor: "var(--accent)",
        animation: "dd-spin .8s linear infinite",
        verticalAlign: -3,
      }}
    />
  );
}

function relativeTime(value: string | null): string {
  if (!value) return "not yet";
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(value)) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? "" : "s"} ago`;
}

function prettyPlatform(value: string): string {
  if (value === "buygoods") return "BuyGoods";
  if (value === "clickbank") return "ClickBank";
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

const eyebrow: React.CSSProperties = {
  color: "var(--accent)",
  fontSize: 11.5,
  fontWeight: 800,
  letterSpacing: ".08em",
};
const surface: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-card)",
  boxShadow: "var(--shadow-card)",
  padding: 24,
};
const progressTrack: React.CSSProperties = {
  height: 7,
  borderRadius: 999,
  background: "var(--surface-2)",
  overflow: "hidden",
  border: "1px solid var(--border)",
};
const progressFill: React.CSSProperties = {
  height: "100%",
  borderRadius: 999,
  background: "var(--accent)",
  transition: "width .25s ease",
};
const stepNav: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  padding: 8,
  display: "flex",
  flexDirection: "column",
  gap: 3,
};
const stepButton: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  width: "100%",
  border: "none",
  background: "transparent",
  color: "var(--body)",
  padding: "8px 9px",
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 12.5,
  fontWeight: 650,
};
const stepButtonActive: React.CSSProperties = {
  background: "color-mix(in srgb, var(--accent) 10%, var(--surface))",
  color: "var(--text)",
};
const stepDot: React.CSSProperties = {
  width: 23,
  height: 23,
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid var(--border)",
  fontSize: 11.5,
  fontWeight: 750,
  flexShrink: 0,
};
const stepDotActive: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--accent-contrast)",
  borderColor: "var(--accent)",
};
const stepDotDone: React.CSSProperties = {
  background: "var(--good)",
  color: "white",
  borderColor: "var(--good)",
};
const muted: React.CSSProperties = { color: "var(--body)", fontSize: 12.5, lineHeight: 1.45 };
const primaryButton: React.CSSProperties = {
  border: "none",
  borderRadius: 8,
  background: "var(--accent)",
  color: "var(--accent-contrast)",
  padding: "10px 16px",
  fontFamily: "inherit",
  fontWeight: 700,
  fontSize: 13.5,
  cursor: "pointer",
};
const primaryButtonSmall: React.CSSProperties = {
  ...primaryButton,
  padding: "7px 12px",
  fontSize: 12.5,
};
const secondaryButton: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: 8,
  background: "var(--surface)",
  color: "var(--body)",
  padding: "8px 13px",
  fontFamily: "inherit",
  fontWeight: 650,
  fontSize: 12.5,
  cursor: "pointer",
};
const inputStyle: React.CSSProperties = {
  boxSizing: "border-box",
  border: "1px solid var(--border)",
  background: "var(--surface-2)",
  color: "var(--text)",
  borderRadius: 7,
  padding: "9px 10px",
  fontFamily: "inherit",
  fontSize: 13,
};
const fieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  fontSize: 13,
  fontWeight: 700,
};
const targetBox: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  border: "1px solid var(--border)",
  background: "var(--surface-2)",
  borderRadius: 10,
  padding: 12,
};
const choiceRow: React.CSSProperties = {
  display: "flex",
  gap: 9,
  alignItems: "flex-start",
  border: "1px solid var(--border)",
  borderRadius: 10,
  background: "var(--surface-2)",
  padding: "11px 12px",
  cursor: "pointer",
};
const choiceRowSelected: React.CSSProperties = {
  borderColor: "var(--accent)",
  background: "color-mix(in srgb, var(--accent) 7%, var(--surface))",
  boxShadow: "0 0 0 1px var(--accent)",
};
const plainRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 14,
  flexWrap: "wrap",
  padding: "15px",
  border: "1px solid var(--border)",
  background: "var(--surface-2)",
  borderRadius: 10,
};
const goodPill: React.CSSProperties = {
  color: "var(--good)",
  background: "color-mix(in srgb, var(--good) 11%, var(--surface))",
  border: "1px solid color-mix(in srgb, var(--good) 30%, var(--border))",
  borderRadius: 999,
  padding: "3px 8px",
  fontSize: 11,
  fontWeight: 700,
  whiteSpace: "nowrap",
};
const workingPill: React.CSSProperties = {
  display: "inline-flex",
  gap: 6,
  alignItems: "center",
  color: "var(--accent)",
  background: "color-mix(in srgb, var(--accent) 9%, var(--surface))",
  border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--border))",
  borderRadius: 999,
  padding: "5px 9px",
  fontSize: 11.5,
  fontWeight: 700,
};
const infoBox: React.CSSProperties = {
  border: "1px solid color-mix(in srgb, var(--accent) 32%, var(--border))",
  background: "color-mix(in srgb, var(--accent) 7%, var(--surface))",
  borderRadius: 10,
  padding: "11px 13px",
  fontSize: 13,
  lineHeight: 1.45,
};
const warningBox: React.CSSProperties = {
  marginTop: 15,
  border: "1px solid color-mix(in srgb, var(--c2) 45%, var(--border))",
  background: "color-mix(in srgb, var(--c2) 8%, var(--surface))",
  borderRadius: 10,
  padding: "11px 13px",
  fontSize: 13,
  lineHeight: 1.45,
};
const workingBox: React.CSSProperties = {
  display: "flex",
  gap: 11,
  alignItems: "center",
  marginTop: 15,
  border: "1px solid color-mix(in srgb, var(--accent) 28%, var(--border))",
  background: "color-mix(in srgb, var(--accent) 6%, var(--surface))",
  borderRadius: 10,
  padding: "12px 14px",
  fontSize: 13,
};
const readyBox: React.CSSProperties = {
  display: "flex",
  gap: 11,
  alignItems: "center",
  marginTop: 15,
  border: "1px solid color-mix(in srgb, var(--good) 40%, var(--border))",
  background: "color-mix(in srgb, var(--good) 8%, var(--surface))",
  borderRadius: 10,
  padding: "12px 14px",
  fontSize: 13,
};
const checkRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 11,
  padding: "11px 12px",
  border: "1px solid var(--border)",
  borderRadius: 10,
  background: "var(--surface-2)",
};
const checkIcon: React.CSSProperties = {
  width: 25,
  height: 25,
  flexShrink: 0,
  borderRadius: "50%",
  border: "1px solid",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  fontSize: 12,
};
const emptyBox: React.CSSProperties = {
  border: "1px dashed var(--border)",
  padding: 16,
  borderRadius: 10,
  color: "var(--body)",
  fontSize: 13,
};
const previewBanner: React.CSSProperties = {
  marginBottom: 14,
  border: "1px dashed var(--accent)",
  background: "color-mix(in srgb, var(--accent) 7%, var(--surface))",
  borderRadius: 9,
  padding: "9px 12px",
  color: "var(--body)",
  fontSize: 12.5,
};
